import { normalizeShipmentContacts } from "../../shipment-contact.schema";
import { shippingTextSchema } from "@/lib/shipping-text";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import {
  useForm,
  useFieldArray,
  type FieldError,
  type FieldErrors,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { UnifiedOrderRequest } from "@/api/unified-orders";
import { AddressBookSelector } from "@/features/address-book/components/AddressBookSelector";
import type { CustomerContact } from "@/features/address-book/types";
import { UnitSelector } from "@/components/unit-selector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Truck, MapPin, Package, User, Plus, Trash2 } from "lucide-react";
import { validateAndGeocodeAddresses } from "@/services/google-geocoding";

interface ShipmentDetailsStepProps {
  data: Partial<UnifiedOrderRequest>;
  onNext: (data: Partial<UnifiedOrderRequest>) => void;
}

export interface ShipmentDetailsStepHandle {
  snapshot: () => Partial<UnifiedOrderRequest>;
}

const addressSchema = z.object({
  street1: shippingTextSchema.min(1, "Street address is required"),
  street2: shippingTextSchema.optional(),
  city: shippingTextSchema.min(1, "City is required"),
  state: z.string().min(1, "State/Province is required"),
  postalCode: z.string().min(1, "Postal code is required"),
  country: z.string().default("CA"),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

const packageSchema = z.object({
  weight: z.number().min(0.1, "Weight is required"),
  weightUnit: z.enum(["g", "kg", "lb", "oz"]).default("g"),
  length: z.number().min(0.1, "Length is required"),
  width: z.number().min(0.1, "Width is required"),
  height: z.number().min(0.1, "Height is required"),
  dimensionUnit: z.enum(["cm", "in"]).default("cm"),
  packageKind: z.enum(["PARCEL", "THICK_ENVELOPE"]).default("PARCEL"),
  description: z.string().optional(),
  isFragile: z.boolean().default(false),
  requiresCooling: z.boolean().default(false),
  declaredValueCents: z.number().optional(),
});

const shipmentSchema = z
  .object({
    // Service type will be selected in the pricing step

    senderName: shippingTextSchema.trim().min(1, "Sender name is required"),
    senderEmail: z
      .string()
      .trim()
      .pipe(z.string().email("Invalid email").or(z.literal("")))
      .optional(),
    senderPhone: z.string().default(""),
    shipFrom: addressSchema,

    recipientName: shippingTextSchema
      .trim()
      .min(1, "Recipient name is required"),
    recipientEmail: z
      .string()
      .trim()
      .pipe(z.string().email("Invalid email").or(z.literal("")))
      .optional(),
    recipientPhone: z.string().default(""),
    shipTo: addressSchema,

    packages: z.array(packageSchema).min(1, "At least one package is required"),

    pickupNotes: z.string().optional(),
    deliveryNotes: z.string().optional(),
  })
  .transform(normalizeShipmentContacts);

const shipmentsSchema = z.object({
  shipments: z.array(shipmentSchema),
});

function emptyShipmentDraft(): z.infer<typeof shipmentSchema> {
  const emptyAddress = () => ({
    street1: "",
    street2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "CA",
    latitude: undefined,
    longitude: undefined,
  });
  return {
    senderName: "",
    senderEmail: "",
    senderPhone: "",
    shipFrom: emptyAddress(),
    recipientName: "",
    recipientEmail: "",
    recipientPhone: "",
    shipTo: emptyAddress(),
    packages: [
      {
        weight: 1000,
        weightUnit: "g",
        length: 30,
        width: 20,
        height: 15,
        dimensionUnit: "cm",
        packageKind: "PARCEL",
        description: "",
        isFragile: false,
        requiresCooling: false,
        declaredValueCents: 0,
      },
    ],
    pickupNotes: "",
    deliveryNotes: "",
  };
}

function SavedAddressErrors({
  name,
  phone,
  email,
  address,
}: {
  name?: FieldError;
  phone?: FieldError;
  email?: FieldError;
  address?: FieldErrors<
    Pick<z.infer<typeof addressSchema>, "street1" | "street2" | "city">
  >;
}) {
  return (
    <div role="alert" className="text-sm text-destructive">
      {name?.message && <p>Name: {name.message}</p>}
      {phone?.message && <p>Phone: {phone.message}</p>}
      {email?.message && <p>Email: {email.message}</p>}
      {(["street1", "street2", "city"] as const).map((field) => {
        const message = address?.[field]?.message;
        return message ? (
          <p key={field}>
            {field}: {message}
          </p>
        ) : null;
      })}
    </div>
  );
}

export const ShipmentDetailsStep = forwardRef<
  ShipmentDetailsStepHandle,
  ShipmentDetailsStepProps
>(function ShipmentDetailsStep({ data, onNext }, ref) {
  const [currentShipment, setCurrentShipment] = useState(0);
  const [useSenderAddressBook, setUseSenderAddressBook] = useState(
    !data.shipments?.some(
      (shipment) =>
        shipment.shipFrom?.street1 && !shipment.shipFromAddressBookEntryId
    )
  );
  const [useRecipientAddressBook, setUseRecipientAddressBook] = useState(
    !data.shipments?.some(
      (shipment) =>
        shipment.shipTo?.street1 && !shipment.shipToAddressBookEntryId
    )
  );
  // Phase 10a.5: per-shipment AddressBook entry IDs. When set, server reuses
  // the entry's Address row so PickupBooking dedup works across orders that
  // picked the same saved sender/recipient.
  const [shipFromEntryIds, setShipFromEntryIds] = useState<
    Record<number, string | undefined>
  >(() =>
    Object.fromEntries(
      data.shipments?.map((shipment, index) => [
        index,
        shipment.shipFromAddressBookEntryId,
      ]) ?? []
    )
  );
  const [shipToEntryIds, setShipToEntryIds] = useState<
    Record<number, string | undefined>
  >(() =>
    Object.fromEntries(
      data.shipments?.map((shipment, index) => [
        index,
        shipment.shipToAddressBookEntryId,
      ]) ?? []
    )
  );
  const shipmentCount =
    (data as { _shipmentCount?: number })?._shipmentCount ??
    data.shipments?.length ??
    1;

  const form = useForm<z.infer<typeof shipmentsSchema>>({
    resolver: zodResolver(shipmentsSchema),
    defaultValues: {
      shipments: Array.from({ length: shipmentCount }, (_, index) => {
        const defaults = emptyShipmentDraft();
        const draft = data.shipments?.[index] as
          | Partial<z.infer<typeof shipmentSchema>>
          | undefined;
        return {
          ...defaults,
          ...draft,
          shipFrom: { ...defaults.shipFrom, ...draft?.shipFrom },
          shipTo: { ...defaults.shipTo, ...draft?.shipTo },
          packages: draft?.packages?.length
            ? draft.packages
            : defaults.packages,
        };
      }),
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      snapshot: () => ({
        shipments: form.getValues("shipments").map((shipment, index) => ({
          ...shipment,
          shipFromAddressBookEntryId: shipFromEntryIds[index],
          shipToAddressBookEntryId: shipToEntryIds[index],
        })) as UnifiedOrderRequest["shipments"],
      }),
    }),
    [form, shipFromEntryIds, shipToEntryIds]
  );

  // Service type will be selected in the pricing step

  // Using useFieldArray but currently only need the form connection
  useFieldArray({
    control: form.control,
    name: "shipments",
  });

  const {
    fields: packageFields,
    append: appendPackage,
    remove: removePackage,
  } = useFieldArray({
    control: form.control,
    name: `shipments.${currentShipment}.packages`,
  });

  const handleAddressBookSelect = useCallback(
    (
      type: "sender" | "recipient",
      shipmentIndex: number,
      contact: CustomerContact
    ) => {
      // A saved selection replaces the contact snapshot, including blank fields.
      form.setValue(
        `shipments.${shipmentIndex}.${type}Name`,
        contact.contactName ?? ""
      );
      form.setValue(
        `shipments.${shipmentIndex}.${type}Email`,
        contact.contactEmail ?? ""
      );
      form.setValue(
        `shipments.${shipmentIndex}.${type}Phone`,
        contact.contactPhone ?? ""
      );
      form.setValue(
        `shipments.${shipmentIndex}.${type === "sender" ? "shipFrom" : "shipTo"}`,
        {
          street1: contact.addressLine1 ?? "",
          street2: contact.addressLine2 ?? "",
          city: contact.addressCity ?? "",
          state: contact.addressProvince ?? "",
          postalCode: contact.addressPostalCode ?? "",
          country: contact.addressCountry,
          latitude: contact.location?.lat,
          longitude: contact.location?.lng,
        }
      );

      // Phase 10a.5: pin the entry ID so the order payload uses entry.addressId
      // instead of creating a fresh Address row at order time.
      if (type === "sender") {
        setShipFromEntryIds((prev) => ({
          ...prev,
          [shipmentIndex]: contact.id,
        }));
      } else {
        form.setValue(
          `shipments.${shipmentIndex}.deliveryNotes`,
          contact.deliveryInstructions ?? ""
        );
        setShipToEntryIds((prev) => ({ ...prev, [shipmentIndex]: contact.id }));
      }
    },
    [form]
  );

  const handleSubmit = async (values: z.infer<typeof shipmentsSchema>) => {
    try {
      // Validate and geocode addresses
      const geocodedShipments = await validateAndGeocodeAddresses(
        values.shipments as unknown as Parameters<
          typeof validateAndGeocodeAddresses
        >[0]
      );

      // Phase 10a.5: forward AddressBook entry IDs alongside the geocoded
      // shipment data so the create payload can prefer entry-id over inline.
      const shipments = geocodedShipments.map((shipment, idx) => {
        return {
          ...shipment,
          shipFromAddressBookEntryId: shipFromEntryIds[idx],
          shipToAddressBookEntryId: shipToEntryIds[idx],
          // carrierId will be set by pricing API response
        };
      });

      onNext({ shipments } as unknown as Parameters<typeof onNext>[0]);
    } catch (error) {
      console.error("Error validating addresses:", error);
      // You might want to show an error toast here
    }
  };

  const copyFromPrevious = (field: keyof z.infer<typeof shipmentSchema>) => {
    if (currentShipment === 0) return;

    const previousShipment = form.getValues(`shipments.${currentShipment - 1}`);
    const value = previousShipment[field];

    if (value) {
      form.setValue(`shipments.${currentShipment}.${field}`, value);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Shipment Details
            </CardTitle>
            <div className="flex items-center gap-2">
              {shipmentCount > 1 && (
                <>
                  <span className="text-sm text-gray-600">
                    Shipment {currentShipment + 1} of {shipmentCount}
                  </span>
                  <div className="flex gap-1">
                    {Array.from({ length: shipmentCount }, (_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setCurrentShipment(index)}
                        className={`w-8 h-8 rounded-full text-xs font-medium ${
                          index === currentShipment
                            ? "bg-blue-500 text-white"
                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              id="shipment-details-form"
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-8"
            >
              {/* Addresses Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <MapPin className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold">Addresses</h3>
                  <span className="text-sm text-muted-foreground">
                    Configure sender and recipient information
                  </span>
                </div>

                <div className="space-y-4">
                  {/* Sender Selection Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Sender</CardTitle>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {useSenderAddressBook
                              ? "Address Book"
                              : "Manual Entry"}
                          </span>
                          <Switch
                            checked={useSenderAddressBook}
                            onCheckedChange={(v) => {
                              setUseSenderAddressBook(v);
                              // Phase 10a.5: switching to Manual detaches from
                              // saved entry — fields become editable.
                              if (!v)
                                setShipFromEntryIds((prev) => ({
                                  ...prev,
                                  [currentShipment]: undefined,
                                }));
                            }}
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {useSenderAddressBook ? (
                        <AddressBookSelector
                          key={`sender-${currentShipment}`}
                          customerId={
                            (data as { placerCustomerId?: string })
                              .placerCustomerId
                          }
                          onSelect={(contact) =>
                            handleAddressBookSelect(
                              "sender",
                              currentShipment,
                              contact
                            )
                          }
                          selectedContactId={shipFromEntryIds[currentShipment]}
                          label="Select from Address Book"
                        />
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          Fill in the sender details below
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {useSenderAddressBook && (
                    <SavedAddressErrors
                      phone={
                        form.formState.errors.shipments?.[currentShipment]
                          ?.senderPhone
                      }
                      email={
                        form.formState.errors.shipments?.[currentShipment]
                          ?.senderEmail
                      }
                      name={
                        form.formState.errors.shipments?.[currentShipment]
                          ?.senderName
                      }
                      address={
                        form.formState.errors.shipments?.[currentShipment]
                          ?.shipFrom
                      }
                    />
                  )}

                  {/* Sender Information Fields */}
                  {!useSenderAddressBook && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <h3 className="font-medium">Sender Information</h3>
                        </div>
                        {currentShipment > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => copyFromPrevious("senderName")}
                          >
                            Copy from previous
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.senderName`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="John Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.senderEmail`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="john@company.com"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.senderPhone`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone *</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="+1-416-555-0123"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Ship From Address */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span className="font-medium">Ship From Address</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipFrom.street1`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Street Address *</FormLabel>
                                <FormControl>
                                  <Input placeholder="123 Main St" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipFrom.street2`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Apt/Unit</FormLabel>
                                <FormControl>
                                  <Input placeholder="Suite 100" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipFrom.city`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>City *</FormLabel>
                                <FormControl>
                                  <Input placeholder="Toronto" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipFrom.state`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Province/State *</FormLabel>
                                <FormControl>
                                  <Input placeholder="ON" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipFrom.postalCode`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Postal Code *</FormLabel>
                                <FormControl>
                                  <Input placeholder="M5V 3A8" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipFrom.country`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Country *</FormLabel>
                                <FormControl>
                                  <Input placeholder="CA" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {/* Hidden fields for coordinates */}
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipFrom.latitude`}
                            render={({ field }) => (
                              <Input type="hidden" {...field} />
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipFrom.longitude`}
                            render={({ field }) => (
                              <Input type="hidden" {...field} />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recipient Selection Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Recipient</CardTitle>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {useRecipientAddressBook
                              ? "Address Book"
                              : "Manual Entry"}
                          </span>
                          <Switch
                            checked={useRecipientAddressBook}
                            onCheckedChange={(v) => {
                              setUseRecipientAddressBook(v);
                              if (!v)
                                setShipToEntryIds((prev) => ({
                                  ...prev,
                                  [currentShipment]: undefined,
                                }));
                            }}
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {useRecipientAddressBook ? (
                        <AddressBookSelector
                          key={`recipient-${currentShipment}`}
                          customerId={
                            (data as { placerCustomerId?: string })
                              .placerCustomerId
                          }
                          onSelect={(contact) =>
                            handleAddressBookSelect(
                              "recipient",
                              currentShipment,
                              contact
                            )
                          }
                          selectedContactId={shipToEntryIds[currentShipment]}
                          label="Select from Address Book"
                          showDeliveryInstructions={false}
                        />
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          Fill in the recipient details below
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {useRecipientAddressBook && (
                    <SavedAddressErrors
                      phone={
                        form.formState.errors.shipments?.[currentShipment]
                          ?.recipientPhone
                      }
                      email={
                        form.formState.errors.shipments?.[currentShipment]
                          ?.recipientEmail
                      }
                      name={
                        form.formState.errors.shipments?.[currentShipment]
                          ?.recipientName
                      }
                      address={
                        form.formState.errors.shipments?.[currentShipment]
                          ?.shipTo
                      }
                    />
                  )}

                  {/* Recipient Information Fields */}
                  {!useRecipientAddressBook && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <h3 className="font-medium">Recipient Information</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.recipientName`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Jane Smith" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.recipientEmail`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="jane@customer.com"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.recipientPhone`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone *</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="+1-604-555-9876"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Ship To Address */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span className="font-medium">Ship To Address</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipTo.street1`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Street Address *</FormLabel>
                                <FormControl>
                                  <Input placeholder="456 Oak Ave" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipTo.street2`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Apt/Unit</FormLabel>
                                <FormControl>
                                  <Input placeholder="Apt 2B" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipTo.city`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>City *</FormLabel>
                                <FormControl>
                                  <Input placeholder="Vancouver" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipTo.state`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Province/State *</FormLabel>
                                <FormControl>
                                  <Input placeholder="BC" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipTo.postalCode`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Postal Code *</FormLabel>
                                <FormControl>
                                  <Input placeholder="V6B 1A1" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipTo.country`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Country *</FormLabel>
                                <FormControl>
                                  <Input placeholder="CA" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {/* Hidden fields for coordinates */}
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipTo.latitude`}
                            render={({ field }) => (
                              <Input type="hidden" {...field} />
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`shipments.${currentShipment}.shipTo.longitude`}
                            render={({ field }) => (
                              <Input type="hidden" {...field} />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Packages Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-primary" />
                    <h3 className="font-semibold">Packages</h3>
                    <span className="text-sm text-muted-foreground">
                      Add package details including weight and dimensions
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      appendPackage({
                        weight: 2.2, // ~1000g in pounds
                        weightUnit: "lb" as const,
                        length: 12, // ~30cm in inches
                        width: 8, // ~20cm in inches
                        height: 6, // ~15cm in inches
                        dimensionUnit: "in" as const,
                        packageKind: "PARCEL" as const,
                        description: "",
                        isFragile: false,
                        requiresCooling: false,
                        declaredValueCents: 0,
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Package
                  </Button>
                </div>

                <div className="space-y-4">
                  {packageFields.map((field, index) => (
                    <Card key={field.id} className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium">Package #{index + 1}</h4>
                        {packageFields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removePackage(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.packageKind`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Package type</FormLabel>
                              <Select
                                value={field.value ?? "PARCEL"}
                                onValueChange={field.onChange}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    onBlur={field.onBlur}
                                    className="w-full data-[size=default]:h-10"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="PARCEL">Parcel</SelectItem>
                                  <SelectItem value="THICK_ENVELOPE">
                                    Thick envelope or padded mailer
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </FormItem>
                          )}
                        />
                        {/* Weight input with unit selector */}
                        <FormItem>
                          <FormLabel>Weight *</FormLabel>
                          <div className="flex gap-2">
                            <FormField
                              control={form.control}
                              name={`shipments.${currentShipment}.packages.${index}.weight`}
                              render={({ field }) => (
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.1"
                                    placeholder="1000"
                                    {...field}
                                    onChange={(e) =>
                                      field.onChange(
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                    className="flex-1"
                                  />
                                </FormControl>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`shipments.${currentShipment}.packages.${index}.weightUnit`}
                              render={({ field }) => (
                                <UnitSelector
                                  type="weight"
                                  value={field.value}
                                  onChange={field.onChange}
                                />
                              )}
                            />
                          </div>
                          <FormMessage />
                        </FormItem>
                        {/* Length */}
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.length`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Length *</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  placeholder="30"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* Width */}
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.width`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Width *</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  placeholder="20"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* Height */}
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.height`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Height *</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  placeholder="15"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      {/* Dimension unit selector */}
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          Dimension Unit:
                        </span>
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.dimensionUnit`}
                          render={({ field }) => (
                            <UnitSelector
                              type="dimension"
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.description`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Description</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Electronics, Books, etc."
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.declaredValueCents`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Declared Value (cents)</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="5000"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseInt(e.target.value, 10) || 0
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="flex gap-4 mt-4">
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.isFragile`}
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2">
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <FormLabel>Fragile</FormLabel>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`shipments.${currentShipment}.packages.${index}.requiresCooling`}
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2">
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <FormLabel>Requires Cooling</FormLabel>
                            </FormItem>
                          )}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Notes Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <MapPin className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold">Notes</h3>
                  <span className="text-sm text-muted-foreground">
                    Add pickup and delivery instructions
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`shipments.${currentShipment}.pickupNotes`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pickup Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Special instructions for pickup..."
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Only used for local delivery services
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`shipments.${currentShipment}.deliveryNotes`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Delivery instructions</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Special instructions for delivery..."
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Only used for local delivery services
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Shipment Navigation - Only show if multiple shipments */}
              {shipmentCount > 1 && (
                <div className="flex items-center justify-center gap-2 pt-6 border-t">
                  {currentShipment > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCurrentShipment(currentShipment - 1)}
                    >
                      Previous Shipment
                    </Button>
                  )}
                  {currentShipment < shipmentCount - 1 && (
                    <Button
                      type="button"
                      onClick={() => setCurrentShipment(currentShipment + 1)}
                    >
                      Next Shipment
                    </Button>
                  )}
                </div>
              )}
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
});
